import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine, EASE } from "../Deck";

const HERO_IMG =
  "https://images.unsplash.com/photo-1645005513713-9e2b92a687d3?q=85&w=1920&auto=format&fit=crop";

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

export default function Slide01Hero({ goTo }) {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 60]);

  return (
    <section
      id="slide-01"
      data-slide={0}
      data-testid="slide-01"
      className="relative min-h-screen w-full snap-start overflow-hidden bg-bone text-inktext"
    >
      <div className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={HERO_IMG}
          alt=""
          style={{ y: yImg }}
          className="h-full w-full scale-[1.12] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/90 to-bone/15 lg:to-bone/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-bone via-transparent to-bone/50" />
      </div>
      <div
        className="grain pointer-events-none absolute inset-0 z-[1] opacity-[0.05]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1500px] items-center px-6 pb-20 pt-24 md:px-16 lg:px-24">
        <div className="w-full">
          <motion.p
            {...fade(0.15)}
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-navy"
          >
            Praniq Healthtech Private Limited — A CYRA Works vertical
          </motion.p>

          <h1 className="mt-7 max-w-4xl font-display text-[clamp(3.1rem,7.6vw,7.4rem)] font-medium leading-[0.98] tracking-[-0.02em]">
            <MaskLine delay={0.25}>Recovery,</MaskLine>
            <MaskLine delay={0.4}>managed</MaskLine>
            <MaskLine delay={0.55}>
              <em className="italic text-gold">end to end.</em>
            </MaskLine>
          </h1>

          <motion.p
            {...fade(0.75)}
            className="mt-8 max-w-md text-lg leading-relaxed text-mut"
          >
            Doctor-ordered. Delivered at home. Outcome-reported.
          </motion.p>

          <motion.svg
            {...fade(0.9)}
            viewBox="0 0 420 56"
            fill="none"
            className="mt-8 h-12 w-[300px] md:w-[360px]"
            aria-hidden="true"
          >
            <motion.path
              d="M2 30 H120 L134 30 141 16 150 46 158 6 166 44 174 30 H248 L262 30 270 22 279 38 288 30 H418"
              stroke="#16355C"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.8, delay: 1, ease: "easeInOut" }}
            />
          </motion.svg>

          <motion.div {...fade(1.05)} className="mt-9 flex flex-wrap gap-4">
            <button
              data-testid="hero-cta-callback"
              onClick={() => goTo(10)}
              className="rounded-full bg-navy px-7 py-3.5 text-sm font-medium text-bone transition-transform duration-200 hover:scale-[1.03] active:scale-[0.97]"
            >
              Request a call back
            </button>
            <button
              data-testid="hero-cta-how"
              onClick={() => goTo(3)}
              className="rounded-full border border-ink/25 bg-bone/60 px-7 py-3.5 text-sm font-medium text-ink backdrop-blur transition-colors duration-200 hover:border-ink/60"
            >
              See how it works
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        {...fade(1.3)}
        className="absolute bottom-8 right-6 z-10 hidden rounded-full border border-line bg-bone/90 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ink backdrop-blur md:block lg:right-24"
      >
        Doctor-ordered care
      </motion.div>

      <motion.div
        {...fade(1.4)}
        className="absolute bottom-7 left-6 z-10 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50 md:left-16 lg:flex lg:left-24"
      >
        Scroll
        <motion.span
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={13} />
        </motion.span>
      </motion.div>
    </section>
  );
}
